"use client";

import React, { useState, useEffect } from "react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { ArrowRight } from "lucide-react";
import Script from "next/script";

const HERO_IMAGES = {
  main: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  dental: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  pt: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  legal: 'https://images.unsplash.com/photo-1589829545856-d10d727437a1?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  hvac: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
  podiatry: 'https://images.unsplash.com/photo-1584433134234-2a4d2e4e4f77?ixlib=rb-4.0.3&q=80&w=2000&auto=format&fit=crop',
} as const;

export default function PurposeAILanding() {
  const [formData, setFormData] = useState({
    name: "",
    practiceType: "",
    websiteUrl: "",
    phone: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedAgent, setSelectedAgent] = useState("cm0h5m4j60002u9upvatef4i0"); // Default to Lisa (Dental)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/api/purposeai/lead", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          practiceType: formData.practiceType,
          websiteUrl: formData.websiteUrl,
          phone: formData.phone,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to submit form");
      }

      setSubmitted(true);
      // Reset form
      setFormData({
        name: "",
        practiceType: "",
        websiteUrl: "",
        phone: "",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit form. Please try again.");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuizClick = () => {
    // Scroll to form or add quiz logic here
    document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* HERO */}
      <header className="relative min-h-screen flex items-center justify-center text-center bg-gradient-to-br from-teal-700 via-emerald-600 to-cyan-700 overflow-hidden">
        <div className="absolute inset-0 bg-black/40"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-25" 
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1521737604893-d14cc237f11d?ixlib=rb-4.0.3&q=80&w=2000')" }}
        ></div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 py-24">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 drop-shadow-2xl">
            Power your team, practice,<br />and purpose with AI
          </h1>
          <p className="text-2xl md:text-3xl text-white/95 mb-12 max-w-4xl mx-auto">
            AI coworkers that work alongside your team — never replace them
          </p>

          <div className="flex flex-col sm:flex-row gap-8 justify-center mb-10">
            <a 
              href="#demo-form" 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="bg-amber-500 hover:bg-amber-400 text-white px-12 py-6 rounded-2xl text-2xl font-bold shadow-2xl hover:scale-105 transition"
            >
              Get My Custom AI Demo (3 min)
            </a>
            <a 
              href="#live" 
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("live")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="border-4 border-white hover:bg-white hover:text-teal-700 text-white px-12 py-6 rounded-2xl text-2xl font-bold transition"
            >
              See It Book a Patient Live
            </a>
          </div>

          <p className="text-xl text-white/90">
            14-day free trial · HIPAA-compliant · No employees replaced — ever
          </p>
        </div>
      </header>

      {/* TRUST BAR */}
      <section className="bg-white py-12 border-b">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-gray-600 mb-6">Trusted by dentists, PTs, lawyers, contractors, and podiatrists nationwide</p>
          <div className="flex flex-wrap justify-center gap-12 items-center opacity-70">
            <span className="text-2xl">🦷 Dentistry</span>
            <span className="text-2xl">🦴 Physical Therapy</span>
            <span className="text-2xl">⚖️ Law Firms</span>
            <span className="text-2xl">🔧 HVAC & Contractors</span>
            <span className="text-2xl">🦶 Podiatry</span>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS – VERTICAL ACCORDION STYLE */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl md:text-6xl font-bold text-center text-gray-900 mb-16">
            How PurposeAI Works For Your Practice
          </h2>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Numbered Steps */}
            <div className="space-y-8">
              {[
                { num: "01", title: "PurposeAI knows your business", desc: "We train your AI coworker on your website, services, pricing, and FAQs in &lt;48 hours." },
                { num: "02", title: "PurposeAI knows your team", desc: "It speaks exactly like your best staff member — warm, professional, and on-brand." },
                { num: "03", title: "PurposeAI knows your locations", desc: "Multi-location? No problem. Routes calls, books appointments, and knows hours for every office." },
                { num: "04", title: "PurposeAI knows your customers", desc: "Remembers returning patients, insurance details, and past visits for personal service." },
                { num: "05", title: "PurposeAI works alongside your team", desc: "Never replaces humans — frees them to focus on care, not phones. More patients seen, more hires made." },
              ].map((step) => (
                <div key={step.num} className="flex gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-teal-600 to-emerald-600 rounded-2xl flex items-center justify-center text-3xl font-bold text-white shadow-lg">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-lg text-gray-600">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Live Demo Selector + iframe */}
            <div className="space-y-8">
              <div className="text-center">
                <h3 className="text-3xl font-bold text-gray-900 mb-6">06. See Your AI Coworker Live</h3>
                <p className="text-xl text-gray-700 mb-8">Pick your industry — watch them book real appointments right now</p>

                {/* Industry Selector */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-lg mx-auto mb-10">
                  {[
                    { name: "Dental", agent: "cm0h5m4j60002u9upvatef4i0", color: "from-pink-500 to-rose-500", label: "Lisa" },
                    { name: "PT", agent: "DAWN_DEMO_ID", color: "from-blue-500 to-cyan-500", label: "Dawn" },
                    { name: "Law", agent: "cm0x1cdnw000114cn3j1emmmg", color: "from-purple-600 to-indigo-600", label: "Rosa" },
                    { name: "HVAC", agent: "TAMMY_DEMO_ID", color: "from-orange-500 to-red-500", label: "Tammy" },
                    { name: "Podiatry", agent: "VICTORIA_DEMO_ID", color: "from-emerald-500 to-teal-600", label: "Victoria" },
                  ].map((ind) => (
                    <button
                      key={ind.name}
                      onClick={() => setSelectedAgent(ind.agent)}
                      className={`p-6 rounded-2xl text-white font-bold text-lg shadow-lg hover:scale-105 transition ${
                        selectedAgent === ind.agent ? 'ring-4 ring-white ring-offset-4 ring-offset-gray-100' : ''
                      } bg-gradient-to-br ${ind.color}`}
                    >
                      {ind.label}<br /><span className="text-sm opacity-90">{ind.name}</span>
                    </button>
                  ))}
                </div>

                {/* Live iframe */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-200">
                  <iframe
                    src={`https://app.easygpt.builders/w/${selectedAgent || "cm0h5m4j60002u9upvatef4i0"}`}
                    className="w-full h-96 md:h-[500px]"
                    frameBorder="0"
                    title="Live PurposeAI Demo"
                  />
                </div>
              </div>

              {/* Final CTA */}
              <div className="text-center bg-gradient-to-r from-teal-600 to-emerald-600 rounded-3xl p-12 text-white">
                <h3 className="text-4xl font-bold mb-6">07. Start for Free — No Card Needed</h3>
                <p className="text-xl mb-8">Get your custom AI coworker + 14-day free trial in &lt;48 hours</p>
                <a
                  href="#demo-form"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="inline-block bg-white text-teal-600 px-12 py-6 rounded-2xl text-2xl font-bold hover:bg-gray-100 transition shadow-xl"
                >
                  Start My Free Trial Now →
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content - Full Width */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

        {/* Why PurposeAI Section */}
        <section id="demo" className="py-12 sm:py-16 text-center" style={{ background: '#f8fafc' }}>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-5" style={{ color: '#0D9488' }}>
            Why PurposeAI Feels Different
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-10 px-4">
            Your mission stays human. The grunt work doesn't.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-5xl mx-auto px-4">
            <div className="text-center">
              <div className="mb-4 text-4xl">🤝</div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0D9488' }}>
                Works alongside your team
              </h3>
              <p className="text-gray-700">
                Works alongside your team to accomplish your purpose
              </p>
            </div>
            <div className="text-center">
              <div className="mb-4 text-4xl">👥</div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0D9488' }}>
                AI coworkers built to support humans
              </h3>
              <p className="text-gray-700">
                AI coworkers built to support humans, not replace them
              </p>
            </div>
            <div className="text-center">
              <div className="mb-4 text-4xl">⚡</div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0D9488' }}>
                Automate tasks, not people
              </h3>
              <p className="text-gray-700">
                Automate tasks, not people
              </p>
            </div>
            <div className="text-center">
              <div className="mb-4 text-4xl">🚀</div>
              <h3 className="text-xl font-semibold mb-3" style={{ color: '#0D9488' }}>
                Automated tasks → Amplified impact
              </h3>
              <p className="text-gray-700">
                Automated tasks → Amplified impact
              </p>
            </div>
          </div>
        </section>

        {/* LIVE DEMOS GRID */}
        <section id="live" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-5xl font-bold text-center text-gray-800 mb-16">
              Meet Your Future AI Coworker — Live Right Now
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-6xl mx-auto">
              {/* Dental – Lisa */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white p-6 text-center">
                  <h3 className="text-2xl font-bold">Lisa – Dental Practices</h3>
                </div>
                <iframe 
                  src="https://app.easygpt.builders/w/cm0h5m4j60002u9upvatef4i0" 
                  className="w-full h-96" 
                  frameBorder="0"
                  title="Lisa - Dental Practices Demo"
                ></iframe>
                <div className="p-6 text-center">
                  <a href="https://dental.purposeai.tech" className="text-teal-600 font-bold hover:underline">
                    → Get Lisa for your dental practice
                  </a>
                </div>
              </div>

              {/* PT – Dawn */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white p-6 text-center">
                  <h3 className="text-2xl font-bold">Dawn – Physical Therapy</h3>
                </div>
                <iframe 
                  src="https://app.easygpt.builders/w/DAWN_DEMO_ID" 
                  className="w-full h-96" 
                  frameBorder="0"
                  title="Dawn - Physical Therapy Demo"
                ></iframe>
                <div className="p-6 text-center">
                  <a href="https://pt.purposeai.tech" className="text-teal-600 font-bold hover:underline">
                    → Get Dawn for your clinic
                  </a>
                </div>
              </div>

              {/* Legal – Rosa */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 text-center">
                  <h3 className="text-2xl font-bold">Rosa – Law Firms</h3>
                </div>
                <iframe 
                  src="https://app.easygpt.builders/w/cm0x1cdnw000114cn3j1emmmg" 
                  className="w-full h-96" 
                  frameBorder="0"
                  title="Rosa - Law Firms Demo"
                ></iframe>
                <div className="p-6 text-center">
                  <a href="https://legal.purposeai.tech" className="text-teal-600 font-bold hover:underline">
                    → Get Rosa for your firm
                  </a>
                </div>
              </div>

              {/* HVAC – Tammy */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 text-center">
                  <h3 className="text-2xl font-bold">Tammy – HVAC & Contractors</h3>
                </div>
                <iframe 
                  src="https://app.easygpt.builders/w/TAMMY_DEMO_ID" 
                  className="w-full h-96" 
                  frameBorder="0"
                  title="Tammy - HVAC & Contractors Demo"
                ></iframe>
                <div className="p-6 text-center">
                  <a href="https://hvac.purposeai.tech" className="text-teal-600 font-bold hover:underline">
                    → Get Tammy for your business
                  </a>
                </div>
              </div>

              {/* Podiatry – Victoria */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white p-6 text-center">
                  <h3 className="text-2xl font-bold">Victoria – Podiatry</h3>
                </div>
                <iframe 
                  src="https://app.easygpt.builders/w/VICTORIA_DEMO_ID" 
                  className="w-full h-96" 
                  frameBorder="0"
                  title="Victoria - Podiatry Demo"
                ></iframe>
                <div className="p-6 text-center">
                  <a href="https://podiatry.purposeai.tech" className="text-teal-600 font-bold hover:underline">
                    → Get Victoria for your practice
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Old See In Action Section - Live Demo - REMOVED */}
        <section className="py-16 sm:py-20 text-center bg-white hidden">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4" style={{ color: '#0D9488', fontSize: '2.8rem' }}>
            See PurposeAI Book a Real Patient Right Now
          </h2>
          <p className="text-lg sm:text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto mb-10 px-4" style={{ fontSize: '1.3rem' }}>
            This is a live PurposeAI receptionist already trained on a dental practice.<br />
            Type anything — ask for whitening, insurance, emergency, or weekend slots.
          </p>

          {/* Live Chat Widget Container */}
          <div 
            id="widget-demo-container"
            className="mx-auto rounded-2xl overflow-hidden shadow-2xl bg-white relative"
            style={{
              maxWidth: '900px',
              minHeight: '650px',
              boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
            }}
          >
            {/* Embedded iframe - will be replaced by widget script if available */}
            <iframe
              id="purposeai-demo-iframe"
              src="https://app.easygpt.builders/w/cmithlpao00017c65wdemqwqe?full=true&scroll=top"
              width="100%"
              height="650px"
              title="Live PurposeAI Receptionist Demo"
              frameBorder="0"
              allow="microphone; camera"
              className="w-full"
              style={{ minHeight: '650px' }}
            />
            
            {/* Fallback message if widget fails */}
            <div 
              id="widget-fallback"
              className="hidden absolute inset-0 flex items-center justify-center bg-gray-50 p-8 text-center rounded-2xl"
            >
              <div>
                <p className="text-lg text-gray-700 mb-4 font-semibold">
                  Live Demo Temporarily Unavailable
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  The widget is experiencing technical difficulties. Please fill out the form below to see your custom AI receptionist demo.
                </p>
                <Button
                  onClick={() => {
                    document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-white"
                  style={{ background: '#0D9488' }}
                >
                  Get My Custom Demo
                </Button>
              </div>
            </div>
          </div>
          
          {/* Load widget script as backup/alternative */}
          <Script
            src="https://app.easygpt.builders/w.js/?id=cmithlpao00017c65wdemqwqe&v=v20251104.4"
            strategy="afterInteractive"
            onLoad={() => {
              console.log('PurposeAI widget script loaded');
            }}
            onError={() => {
              console.error('PurposeAI widget script failed to load - using iframe fallback');
            }}
          />
          
          {/* Second CTA - Direct action after demo */}
          <div className="text-center mt-10">
            <button
              onClick={() => {
                document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold text-xl px-10 py-6 rounded-2xl shadow-2xl transform hover:scale-105 transition"
            >
              Hire this AI coworker for your practice →
            </button>
            <p className="mt-4 text-gray-600">
              Takes 3 minutes · 14-day free trial · No employees replaced
            </p>
          </div>
          
          {/* Error detection for iframe */}
          <Script id="widget-error-handler" strategy="afterInteractive">
            {`
              (function() {
                const iframe = document.getElementById('purposeai-demo-iframe');
                const fallback = document.getElementById('widget-fallback');
                
                if (!iframe || !fallback) return;
                
                // Check for iframe load errors
                iframe.addEventListener('error', function() {
                  console.error('Iframe load error detected');
                  fallback.classList.remove('hidden');
                });
                
                // Check if iframe loaded successfully after a delay
                iframe.addEventListener('load', function() {
                  setTimeout(function() {
                    try {
                      // Try to access iframe content (will fail if cross-origin, which is expected)
                      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                      if (iframeDoc) {
                        const bodyText = iframeDoc.body?.innerHTML || '';
                        if (bodyText.includes('500') || bodyText.includes('Internal Server Error') || bodyText.includes('Error')) {
                          fallback.classList.remove('hidden');
                        }
                      }
                    } catch (e) {
                      // Cross-origin - can't check content, assume it's working
                      // This is expected behavior for iframes from different domains
                    }
                  }, 2000);
                });
                
                // Fallback: Check after 5 seconds if iframe seems broken
                setTimeout(function() {
                  try {
                    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
                    if (iframeDoc && iframeDoc.body) {
                      const bodyText = iframeDoc.body.innerHTML || '';
                      if (bodyText.includes('500') || bodyText.includes('Error') || (bodyText.trim().length < 100 && !bodyText.includes('html'))) {
                        fallback.classList.remove('hidden');
                      }
                    }
                  } catch (e) {
                    // Cross-origin - can't check, assume working
                  }
                }, 5000);
              })();
            `}
          </Script>

          <p className="mt-8 text-gray-600 text-base sm:text-lg px-4 max-w-2xl mx-auto">
            ↑ This exact AI will be customized with <strong>your</strong> website, pricing, calendar, and mission in &lt;48 hours.
          </p>

          <a 
            href="#demo-form"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("demo-form")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-block mt-5 text-white font-bold rounded-lg text-lg sm:text-xl transition-colors"
            style={{
              background: '#F59E0B',
              padding: '16px 32px',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#d97706'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F59E0B'}
          >
            Build My Custom AI Staff →
          </a>
        </section>
        {/* End of old section */}

        {/* Info Section */}
        <section className="text-center my-12 sm:my-16">
          <p className="text-base sm:text-lg text-gray-700 mb-6">
            Works for: <strong>Dentistry</strong> · <strong>Physical Therapy</strong> · <strong>Surgery</strong> · <strong>Law</strong> · <strong>Home Services</strong>
          </p>
          <div className="my-8 space-y-2">
            <p className="text-lg sm:text-xl text-gray-800">
              Pricing: <strong style={{ color: '#0D9488' }}>$399/mo</strong> — unlimited calls + chats
            </p>
            <p className="text-base sm:text-lg text-gray-700">
              <strong>No employees replaced — ever.</strong>
            </p>
          </div>
        </section>

        {/* Demo Form */}
        <form 
          id="demo-form"
          onSubmit={handleSubmit}
          className="max-w-lg mx-auto my-12 sm:my-16 space-y-4"
        >
          <Input
            type="text"
            name="name"
            placeholder="Name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full h-12 text-base"
            required
            disabled={isSubmitting || submitted}
          />
          <Input
            type="text"
            name="practiceType"
            placeholder="Practice Type"
            value={formData.practiceType}
            onChange={handleInputChange}
            className="w-full h-12 text-base"
            required
            disabled={isSubmitting || submitted}
          />
          <Input
            type="url"
            name="websiteUrl"
            placeholder="Website URL"
            value={formData.websiteUrl}
            onChange={handleInputChange}
            className="w-full h-12 text-base"
            required
            disabled={isSubmitting || submitted}
          />
          <Input
            type="tel"
            name="phone"
            placeholder="Phone"
            value={formData.phone}
            onChange={handleInputChange}
            className="w-full h-12 text-base"
            required
            disabled={isSubmitting || submitted}
          />
          {error && (
            <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded">
              {error}
            </div>
          )}
          <Button
            type="submit"
            className="w-full text-white px-6 py-4 text-lg rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: '#F59E0B' }}
            onMouseEnter={(e) => {
              if (!isSubmitting && !submitted) {
                e.currentTarget.style.background = '#d97706';
              }
            }}
            onMouseLeave={(e) => {
              if (!isSubmitting && !submitted) {
                e.currentTarget.style.background = '#F59E0B';
              }
            }}
            size="lg"
            disabled={isSubmitting || submitted}
          >
            {isSubmitting ? "Sending..." : submitted ? "Submitted!" : "Get My Custom Demo →"}
          </Button>
          {submitted && (
            <p className="text-center mt-4" style={{ color: '#0D9488' }}>
              Thanks! We'll email your custom AI blueprint in 24h. Questions? Reply here.
            </p>
          )}
        </form>
      </main>
    </div>
  );
}

